import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { userAPI, testProgressAPI } from '../utils/api'
import { styleFromServer, colorFromServer, bodyFromServer, fashionFromServer, bodyLabel } from '../utils/profileFromServer'
import { useServerProfile } from '../hooks/useServerProfile'
import { userScopedKey } from '../utils/userStorage'
import { getStylePortraitSrc } from '../utils/styleImages'
import { STYLE_OPTIONS, styleLabelOf } from '../utils/fashionStyleOptions'

const C = {
  gold: '#B8973A', border: '#e8e8e4', muted: '#999999',
  body: '#666666', h1: '#111111', bg: '#faf9f7',
}

interface StyleProfile {
  ageRange: string
  bodyType: string
  styleDirections: string[]
  budget: string
  concerns: string[]
  skinTone: string
  scenes: string[]
}

// aiffd_style_result 存的形状（StyleTestPage.tsx 里 JSON.stringify 的对象）
interface StyleResultData {
  family: string
  variant: string
  styleInfo: { cn: string; en: string; family: string; familyEn: string; element: string }
}

const ELEMENT_COLORS: Record<string, string> = {
  '木': '#6B8A4A', '火': '#C0392B', '土': '#8B7355', '金': '#B8973A', '水': '#2C5F8A',
}

const WARMCOOL_LABELS: Record<string, string> = {
  warm: '暖调', cool: '冷调',
  neutral_warm: '中性偏暖', neutral_cool: '中性偏冷', olive: '橄榄灰黄',
}

function SectionTitle({ label }: { label: string }) {
  return (
    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', letterSpacing: '3px', color: C.gold, textTransform: 'uppercase' as const, marginBottom: '16px' }}>
      {label}
    </p>
  )
}

function EmptyBadge({ label, to }: { label: string; to: string }) {
  return (
    <Link to={to} style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      fontFamily: 'Inter, sans-serif', fontSize: '12px', color: C.muted,
      border: `1px dashed ${C.border}`, padding: '6px 14px', borderRadius: '4px',
      textDecoration: 'none',
    }}>
      <span style={{ fontSize: '14px' }}>+</span> {label}
    </Link>
  )
}

// 邮箱首字母头像（没选预设头像时的兜底显示）
function AvatarInitial({ text }: { text: string }) {
  const letter = (text || '?').trim().charAt(0).toUpperCase()
  return (
    <div style={{
      width: '64px', height: '64px', borderRadius: '50%', background: C.gold,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <span style={{ fontFamily: 'Georgia, serif', fontSize: '26px', color: '#fff' }}>{letter}</span>
    </div>
  )
}

// 预设头像方案：颜色 + 符号组合，不需要真实上传图片文件
const AVATAR_PRESETS: { id: string; color: string; symbol: string }[] = [
  { id: 'gold-star', color: '#B8973A', symbol: '✦' },
  { id: 'rose', color: '#B85C6E', symbol: '◈' },
  { id: 'sage', color: '#6B8A6A', symbol: '◇' },
  { id: 'plum', color: '#7A5C8A', symbol: '●' },
  { id: 'ink', color: '#3A3A3A', symbol: '▲' },
  { id: 'teal', color: '#4A7A7A', symbol: '◆' },
]

function AvatarDisplay({ presetId, fallbackText, size = 64 }: { presetId: string | null; fallbackText: string; size?: number }) {
  const preset = presetId ? AVATAR_PRESETS.find(p => p.id === presetId) : null
  if (preset) {
    return (
      <div style={{
        width: `${size}px`, height: `${size}px`, borderRadius: '50%', background: preset.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <span style={{ fontSize: `${Math.round(size * 0.4)}px`, color: '#fff' }}>{preset.symbol}</span>
      </div>
    )
  }
  return <AvatarInitial text={fallbackText} />
}

export default function ProfilePage() {
  const { user, updateUser } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const u = user as Record<string, unknown> | null
  // 后端返回的字段是 name，不是 username——这里统一用 name，没有的话退回邮箱前缀
  const displayName = (u?.name as string) || (u?.email as string)?.split('@')[0] || '用户'
  const email = (u?.email ?? '') as string

  // 从 OnboardingPage 主入口跳过来的：说明用户已经全部测完了，还点了"开始测试"，
  // 这里弹窗问一下是要调整某一项还是全部重测，而不是默默地什么都不做
  const [showRetestPrompt, setShowRetestPrompt] = useState(!!(location.state as { showRetestPrompt?: boolean } | null)?.showRetestPrompt)

  // ── 头像：预设图案方案，选择结果存本地（带用户前缀），不经过后端 ──
  const avatarKey = userScopedKey('aiffd_avatar_choice', user)
  const [avatarId, setAvatarId] = useState<string | null>(() => localStorage.getItem(avatarKey))
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false)
  const pickAvatar = (id: string) => {
    localStorage.setItem(avatarKey, id)
    setAvatarId(id)
    setAvatarPickerOpen(false)
  }

  // ── 用户名编辑：真实调用后端 PATCH /user/me ──
  const [editingName, setEditingName] = useState(false)
  const [nameInput, setNameInput] = useState(displayName)
  const [savingName, setSavingName] = useState(false)
  const [nameError, setNameError] = useState('')

  useEffect(() => { setNameInput(displayName) }, [displayName])

  const saveName = async () => {
    const trimmed = nameInput.trim()
    if (!trimmed) { setNameError('用户名不能为空'); return }
    setSavingName(true)
    setNameError('')
    try {
      await userAPI.updateMe({ name: trimmed })
      updateUser({ name: trimmed })
      setEditingName(false)
    } catch (err: any) {
      setNameError(err?.response?.data?.error || '保存失败，请重试')
    } finally {
      setSavingName(false)
    }
  }

  // ── 云端档案（2026-10-22 新增）────────────────────────
  // 读取顺序：本机优先、云端补缺。本机有的结果照常显示；本机没有的（换设备、清缓存、换浏览器），
  // 用云端档案换算出的数据补上。读取失败时页面照常只显示本机数据。
  const serverProfile = useServerProfile(user)

  // ── 档案数据读取 ──────────────────────────────────
  const raw = localStorage.getItem(userScopedKey('aiffd_profile', user))
  const localProfile: StyleProfile | null = raw ? (() => { try { return JSON.parse(raw) } catch { return null } })() : null
  // 年龄段以云端为准（Onboarding 采集，取值 18-24 等）；本机旧存档里的“30–39岁”只作兜底
  const ageRange = (typeof serverProfile?.ageRange === 'string' && serverProfile.ageRange) || localProfile?.ageRange || ''
  const profile: StyleProfile | null = localProfile || ageRange
    ? { ...(localProfile ?? { bodyType: '', styleDirections: [], budget: '', concerns: [], skinTone: '', scenes: [] }), ageRange }
    : null

  const serverColor = colorFromServer(serverProfile)
  const localWarmCool = localStorage.getItem(userScopedKey('aiffd_warmcool', user)) || ''
  const localSeasonName = localStorage.getItem(userScopedKey('aiffd_season_name', user)) || ''
  // 色彩各层按“同一次测试”整组取用：本机有季型就整组用本机，避免本机季型与云端副气拼在一起
  const colorFromLocal = !!localSeasonName || (!!localWarmCool && !serverColor.seasonName)
  const warmCool = colorFromLocal ? localWarmCool : (localWarmCool || serverColor.warmCool)
  const seasonName = colorFromLocal ? localSeasonName : serverColor.seasonName
  const seasonElement = colorFromLocal ? (localStorage.getItem(userScopedKey('aiffd_season_element', user)) || '') : serverColor.seasonElement
  const elementName = colorFromLocal ? (localStorage.getItem(userScopedKey('aiffd_element_name', user)) || '') : serverColor.elementName
  const finalSeason25 = colorFromLocal ? (localStorage.getItem(userScopedKey('aiffd_25season', user)) || '') : serverColor.finalSeason25

  const bodyRaw = localStorage.getItem(userScopedKey('aiffd_body_result', user))
  const localBody = bodyRaw ? (() => { try { return JSON.parse(bodyRaw) } catch { return null } })() : null
  const bodyResult = localBody ?? bodyFromServer(serverProfile)

  const styleRaw = localStorage.getItem(userScopedKey('aiffd_style_result', user))
  const localStyle: StyleResultData | null = styleRaw
    ? (() => { try { return JSON.parse(styleRaw) as StyleResultData } catch { return null } })()
    : null
  const styleResult: StyleResultData | null = localStyle ?? styleFromServer(serverProfile)

  // 五官详情（StyleTestPage 存的 { [维度id]: { label, value } } 结构）
  const faceRaw = localStorage.getItem(userScopedKey('aiffd_face_result', user))
  const localFace: Record<string, { label: string; value: string }> | null = faceRaw
    ? (() => { try { return JSON.parse(faceRaw) } catch { return null } })()
    : null
  // 2026-10-23：本机没有时读云端测试存档（面部测试完成时会把五官详情一起存进 test_progress 的 style 记录）
  const [serverFace, setServerFace] = useState<Record<string, { label: string; value: string }> | null>(null)
  useEffect(() => {
    setServerFace(null)
    if (!user?.id || faceRaw) return
    let cancelled = false
    testProgressAPI.get('style')
      .then(res => {
        const detail = res.data?.progress?.data?.faceDetail
        if (!cancelled && detail && typeof detail === 'object') setServerFace(detail)
      })
      .catch(err => console.error('[profile] 读取云端五官详情失败', err))
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, !!faceRaw])
  const faceResult = localFace ?? serverFace
  const styleDisplayName = styleResult?.styleInfo?.cn || styleResult?.variant || ''
  const portraitSrc = getStylePortraitSrc(styleDisplayName)

  // 个人时尚选择（目前只上线了"理想形象" Q1-Q3）
  const fashionRaw = localStorage.getItem(userScopedKey('aiffd_fashion_style', user))
  const localFashion: {
    aspired_style_primary: string | null
    aspired_style_secondary: string[]
    current_style: string[]
    current_aspired_style_gap: 'stable' | 'gap' | 'no_fixed_style'
    rejected_style_codes: string[]
  } | null = fashionRaw ? (() => { try { return JSON.parse(fashionRaw) } catch { return null } })() : null
  const fashionResult = localFashion ?? fashionFromServer(serverProfile)

  // "最想成为的样子"（Q1 的最喜欢选项）对应的插画，用于个人时尚选择区块右侧的卡片展示
  const aspiredStyleCards = fashionResult
    ? [
        ...(fashionResult.aspired_style_primary ? [fashionResult.aspired_style_primary] : []),
        ...fashionResult.aspired_style_secondary,
      ]
        .map(id => ({ id, ...STYLE_OPTIONS.find(o => o.id === id) }))
        .filter((o): o is { id: string; label: string; desc: string; img: string } => !!o.img)
    : []

  // 完整度计算：按 AIFFD 产品架构文档 3.2 节的比例（形45% + 色30% + 意20% + 合5%）
  const formPct = styleResult ? 45 : bodyResult ? 22 : 0
  const colorPct = (warmCool ? 10 : 0) + (seasonName ? 10 : 0) + (finalSeason25 ? 10 : 0)
  const preferencePct = fashionResult ? 4 : 0
  const generationPct = (styleResult && finalSeason25) ? 5 : 0
  const completionPct = formPct + colorPct + preferencePct + generationPct

  // 是否已订阅付费方案：风格档案图谱（品牌匹配 + 优惠券）是 AIFFD 商业化的核心，
  // 免费用户点进来之前要先引导去订阅页，而不是直接看到完整图谱。
  // 2026-09-13 修复：原来用的 u?.subscriptionTier 是占位字段名，后端从未真正返回过这个字段。
  // 已确认后端 routes/auth.ts 的 /register 和 /login 接口返回的用户对象上真实字段叫
  // membershipTier，注册时默认值是 "free"。原判断里额外排除的 'newsletter' 这个值
  // 没有任何后端依据（当初是照着占位字段瞎猜的），这次一并去掉；如果实际存在"订阅了免费
  // newsletter 但不算真正付费会员"这种中间状态，需要看过 routes/subscription.ts 后再补上对应真实值。
  const isSubscribed = !!(u?.membershipTier && u.membershipTier !== 'free')

  // "全部重新测试"：清空体型/风格/五官/色彩三层的存档，回到体型测试第一步重新开始
  const restartAllTests = () => {
    const keysToClear = [
      'aiffd_body_result', 'aiffd_qixue_result', 'aiffd_style_result', 'aiffd_face_result',
      'aiffd_warmcool', 'aiffd_color_result', 'aiffd_season_result', 'aiffd_season_name',
      'aiffd_season_element', 'aiffd_element_result', 'aiffd_element_name', 'aiffd_25season',
    ]
    keysToClear.forEach(k => localStorage.removeItem(userScopedKey(k, user)))
    setShowRetestPrompt(false)
    navigate('/test/body')
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>

      {/* 全部测完后重新点"开始测试"跳过来的确认弹窗 */}
      {showRetestPrompt && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,15,13,0.5)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
        }}>
          <div style={{ background: '#fff', borderRadius: '10px', padding: '32px', maxWidth: '420px', width: '100%' }}>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', letterSpacing: '3px', color: C.gold, marginBottom: '10px' }}>你已经完成过全部测试</p>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: C.h1, fontWeight: 400, margin: '0 0 12px' }}>要调整哪一项，还是全部重测？</h2>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: C.body, lineHeight: 1.8, marginBottom: '24px' }}>
              这是你完整的风格档案。如果某一项测得不准，可以单独重做那一项；也可以全部推倒重来。
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Link to="/test/body" onClick={() => setShowRetestPrompt(false)} style={{
                background: C.gold, color: '#fff', border: 'none', borderRadius: '4px', textAlign: 'center' as const,
                padding: '12px', fontFamily: 'Inter, sans-serif', fontSize: '13px', textDecoration: 'none',
              }}>重新做体型 / 风格测试</Link>
              <Link to="/test/color" onClick={() => setShowRetestPrompt(false)} style={{
                background: '#f5f0e8', color: C.h1, border: 'none', borderRadius: '4px', textAlign: 'center' as const,
                padding: '12px', fontFamily: 'Inter, sans-serif', fontSize: '13px', textDecoration: 'none',
              }}>重新做色彩测试</Link>
              <button onClick={restartAllTests} style={{
                background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '4px',
                padding: '12px', fontFamily: 'Inter, sans-serif', fontSize: '13px', color: C.body, cursor: 'pointer',
              }}>全部重新测试</button>
              <button onClick={() => setShowRetestPrompt(false)} style={{
                background: 'none', border: 'none', padding: '8px', fontFamily: 'Inter, sans-serif', fontSize: '12px', color: C.muted, cursor: 'pointer',
              }}>不用了，档案就这样挺好</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '64px 24px' }}>

        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', marginBottom: '24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', color: C.muted, letterSpacing: '1px' }}>
              当前档案完成度
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '120px', height: '3px', background: C.border, borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${completionPct}%`, background: C.gold, transition: 'width .4s ease' }} />
              </div>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', color: C.gold, fontWeight: 600 }}>{completionPct}%</span>
            </div>
          </div>
        </div>


        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', borderBottom: `1px solid ${C.border}`, paddingBottom: '32px', marginBottom: '40px', position: 'relative' }}>
          {/* 头像：点击打开预设图案选择器 */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setAvatarPickerOpen(o => !o)}
              style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', borderRadius: '50%' }}
              aria-label="更换头像"
            >
              <AvatarDisplay presetId={avatarId} fallbackText={email} />
            </button>
            {avatarPickerOpen && (
              <div style={{
                position: 'absolute', top: '72px', left: 0, zIndex: 10,
                background: '#fff', border: `1px solid ${C.border}`, borderRadius: '10px',
                padding: '14px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', width: '180px',
              }}>
                {AVATAR_PRESETS.map(p => (
                  <button key={p.id} onClick={() => pickAvatar(p.id)} style={{
                    border: avatarId === p.id ? `2px solid ${C.gold}` : '2px solid transparent',
                    background: 'none', padding: '2px', cursor: 'pointer', borderRadius: '50%',
                  }}>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '50%', background: p.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: '16px', color: '#fff' }}>{p.symbol}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ flex: 1 }}>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', letterSpacing: '3px', color: C.gold, marginBottom: '8px' }}>个人风格档案</p>
            {editingName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' as const }}>
                <input
                  value={nameInput}
                  onChange={e => { setNameInput(e.target.value); setNameError('') }}
                  onKeyDown={e => { if (e.key === 'Enter') saveName() }}
                  style={{
                    fontFamily: 'Georgia, serif', fontSize: '22px', color: C.h1,
                    border: `1px solid ${nameError ? '#dc2626' : C.gold}`, borderRadius: '4px',
                    padding: '4px 10px', outline: 'none', maxWidth: '240px',
                  }}
                  autoFocus
                />
                <button onClick={saveName} disabled={savingName} style={{
                  background: C.gold, color: '#fff', border: 'none', borderRadius: '4px',
                  padding: '8px 16px', fontFamily: 'Inter, sans-serif', fontSize: '12px', cursor: savingName ? 'not-allowed' : 'pointer',
                }}>{savingName ? '保存中…' : '保存'}</button>
                <button onClick={() => { setEditingName(false); setNameInput(displayName); setNameError('') }} style={{
                  background: 'none', border: `1px solid ${C.border}`, borderRadius: '4px',
                  padding: '8px 16px', fontFamily: 'Inter, sans-serif', fontSize: '12px', color: C.muted, cursor: 'pointer',
                }}>取消</button>
                {nameError && <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#dc2626', width: '100%' }}>{nameError}</span>}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '28px', fontWeight: 400, color: C.h1, margin: 0 }}>{displayName}</h1>
                <button onClick={() => setEditingName(true)} style={{
                  background: 'none', border: `1px dashed ${C.border}`, borderRadius: '4px',
                  padding: '4px 10px', fontFamily: 'Inter, sans-serif', fontSize: '11px', color: C.muted, cursor: 'pointer',
                }}>编辑用户名</button>
              </div>
            )}
          </div>
        </div>

        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '28px', marginBottom: '24px' }}>
          <SectionTitle label="我的风格" />

          {styleResult || bodyResult ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

              {styleResult ? (
                <div style={{ display: 'grid', gridTemplateColumns: portraitSrc ? '140px 1fr' : '1fr', gap: '20px', alignItems: 'center' }}>
                  {portraitSrc && (
                    <img src={portraitSrc} alt={styleDisplayName} style={{ width: '100%', borderRadius: '8px', border: `1px solid ${C.border}`, objectFit: 'cover' }} />
                  )}
                  <div>
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', letterSpacing: '2px', color: C.muted, marginBottom: '6px' }}>风格定位</p>
                    <p style={{ fontFamily: 'Georgia, serif', fontSize: '26px', color: C.gold, margin: '0 0 6px' }}>{styleDisplayName}</p>
                    {styleResult.styleInfo?.family && (
                      <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: C.muted, margin: 0 }}>
                        {styleResult.styleInfo.family}（{styleResult.styleInfo.familyEn}）家族 · 五行属{styleResult.styleInfo.element}
                      </p>
                    )}
                    {!portraitSrc && (
                      <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: C.muted, marginTop: '8px' }}>模特图片素材准备中</p>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: C.muted, marginBottom: '12px' }}>体型测试已完成，继续完成面部测试即可得出完整风格结论</p>
                  <Link to="/test/style" style={{ display: 'inline-block', background: C.gold, color: '#fff', padding: '10px 24px', fontFamily: 'Inter, sans-serif', fontSize: '12px', letterSpacing: '2px', textDecoration: 'none', borderRadius: '4px' }}>
                    继续面部测试
                  </Link>
                </div>
              )}

              {bodyResult && (
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '20px' }}>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', letterSpacing: '2px', color: C.gold, marginBottom: '14px' }}>体型详细信息</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1px', background: C.border }}>
                    {[
                      { label: '身高区间', value: bodyResult.height },
                      { label: '骨架', value: Array.isArray(bodyResult.boneScale) ? bodyResult.boneScale.map(bodyLabel).join(' · ') : bodyResult.boneScale },
                      { label: '肩形', value: Array.isArray(bodyResult.shoulder) ? bodyResult.shoulder.map(bodyLabel).join(' + ') : null },
                      { label: '腰型', value: Array.isArray(bodyResult.waist) ? bodyResult.waist.map(bodyLabel).join(' + ') : null },
                      { label: '体型', value: Array.isArray(bodyResult.bodyShape) ? bodyResult.bodyShape.map(bodyLabel).join(' + ') : null },
                      { label: '皮肉质', value: Array.isArray(bodyResult.fleshTexture) ? bodyResult.fleshTexture.map(bodyLabel).join(' + ') : null },
                    ].filter(item => item.value).map((item, i) => (
                      <div key={i} style={{ background: '#fff', padding: '16px' }}>
                        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', color: C.muted, margin: '0 0 4px' }}>{item.label}</p>
                        <p style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: C.h1, margin: 0 }}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {styleResult && (
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '20px' }}>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', letterSpacing: '2px', color: C.gold, marginBottom: '14px' }}>五官详细信息</p>
                  {faceResult ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1px', background: C.border }}>
                      {Object.values(faceResult).map((item, i) => (
                        <div key={i} style={{ background: '#fff', padding: '16px' }}>
                          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', color: C.muted, margin: '0 0 4px' }}>{item.label}</p>
                          <p style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: C.h1, margin: 0 }}>{item.value}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: C.muted, margin: 0, lineHeight: 1.8 }}>
                      重新做一次面部测试，即可在这里看到五官详情。
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: C.muted, marginBottom: '16px' }}>还没有开始风格测试</p>
              <Link to="/test/body" style={{ display: 'inline-block', background: C.gold, color: '#fff', padding: '10px 24px', fontFamily: 'Inter, sans-serif', fontSize: '12px', letterSpacing: '2px', textDecoration: 'none', borderRadius: '4px' }}>
                开始体型测试
              </Link>
            </div>
          )}
        </div>

        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '28px', marginBottom: '24px' }}>
          <SectionTitle label="色彩档案" />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1px', background: C.border, marginBottom: '16px' }}>
            {[
              { label: '冷暖底调', value: WARMCOOL_LABELS[warmCool] || null, to: '/test/color', tag: '第一层' },
              { label: '五季主型', value: seasonName ? `${seasonName}（季节自带·${seasonElement}）` : null, to: '/test/color/season', tag: '第二层' },
              { label: '东方 25 季', value: finalSeason25 || null, to: '/test/color/element', tag: '第三层' },
            ].map((item, i) => (
              <div key={i} style={{ background: '#fff', padding: '20px' }}>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', letterSpacing: '2px', color: C.muted, marginBottom: '8px' }}>{item.tag} · {item.label}</p>
                {item.value ? (
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: '18px', color: C.gold, margin: 0 }}>{item.value}</p>
                ) : (
                  <EmptyBadge label={`完成${item.tag}测试`} to={item.to} />
                )}
              </div>
            ))}
          </div>

          {(seasonName || elementName) && (
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: C.muted, lineHeight: 1.8, marginBottom: '20px', background: '#faf8f4', padding: '10px 14px', borderRadius: '6px' }}>
              💡 "季节自带"的五行是季节本身固有的属性（比如秋天固有属性是金），跟你个人测出的"副气"是两回事——两者组合在一起，才是你的东方 25 季分类（比如"秋水"＝秋季 + 你个人的水副气）。
            </p>
          )}

          {(elementName || seasonName) && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {elementName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: ELEMENT_COLORS[elementName] || C.gold,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: '#fff' }}>{elementName}</span>
                  </div>
                  <div>
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', color: C.muted, margin: '0 0 2px' }}>个人五行副气</p>
                    <p style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: C.h1, margin: 0 }}>{elementName}副气</p>
                  </div>
                </div>
              )}
              {!elementName && seasonName && (
                <EmptyBadge label="进入副气测试 → 25季" to="/test/color/element" />
              )}
            </div>
          )}

          {!warmCool && !seasonName && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: C.muted, marginBottom: '16px' }}>色彩档案尚未建立</p>
              <Link to="/test/color" style={{ display: 'inline-block', background: C.gold, color: '#fff', padding: '10px 24px', fontFamily: 'Inter, sans-serif', fontSize: '12px', letterSpacing: '2px', textDecoration: 'none', borderRadius: '4px' }}>
                开始色彩测试
              </Link>
            </div>
          )}
        </div>

        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '28px', marginBottom: '24px' }}>
          <SectionTitle label="个人时尚选择" />
          {fashionResult ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '24px' }}>
                <div>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', letterSpacing: '2px', color: C.muted, marginBottom: '10px' }}>最想成为的样子</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {fashionResult.aspired_style_primary && (
                      <span style={{ border: `1.5px solid ${C.gold}`, background: '#fdf8ee', color: C.gold, padding: '5px 12px', borderRadius: '20px', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>
                        ★ {styleLabelOf(fashionResult.aspired_style_primary)}
                      </span>
                    )}
                    {fashionResult.aspired_style_secondary.map(id => (
                      <span key={id} style={{ border: `1px solid ${C.border}`, color: C.body, padding: '5px 12px', borderRadius: '20px', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>
                        {styleLabelOf(id)}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: '24px' }}>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', letterSpacing: '2px', color: C.muted, marginBottom: '10px' }}>实际最常穿</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {fashionResult.current_aspired_style_gap === 'no_fixed_style' ? (
                      <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: C.muted }}>没有固定风格</span>
                    ) : fashionResult.current_style.map(id => (
                      <span key={id} style={{ border: `1px solid ${C.border}`, color: C.body, padding: '5px 12px', borderRadius: '20px', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>
                        {styleLabelOf(id)}
                      </span>
                    ))}
                  </div>
                </div>
                {fashionResult.rejected_style_codes.length > 0 && !fashionResult.rejected_style_codes.includes('no_rejected_style') && (
                  <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: '24px' }}>
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', letterSpacing: '2px', color: C.muted, marginBottom: '10px' }}>明确不喜欢</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {fashionResult.rejected_style_codes.map(id => (
                        <span key={id} style={{ background: '#f5f5f3', color: C.body, padding: '5px 12px', borderRadius: '20px', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>
                          {styleLabelOf(id)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {aspiredStyleCards.length > 0 && (
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '20px' }}>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', letterSpacing: '2px', color: C.muted, marginBottom: '12px' }}>最想成为的样子 · 参考穿搭</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '12px' }}>
                    {aspiredStyleCards.map(o => {
                      const isPrimary = o.id === fashionResult.aspired_style_primary
                      return (
                        <div key={o.id} style={{
                          border: `1px solid ${isPrimary ? C.gold : C.border}`,
                          borderRadius: '8px', overflow: 'hidden', position: 'relative',
                        }}>
                          {isPrimary && (
                            <span style={{
                              position: 'absolute', top: '4px', right: '4px', fontSize: '13px', color: C.gold,
                              background: 'rgba(255,255,255,0.85)', borderRadius: '50%', width: '20px', height: '20px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>★</span>
                          )}
                          <img src={o.img} alt={o.label} style={{ width: '100%', height: 'auto', display: 'block' }} />
                          <p style={{
                            fontFamily: 'Inter, sans-serif', fontSize: '11px', margin: 0, padding: '6px 4px',
                            textAlign: 'center' as const, color: isPrimary ? C.gold : C.body,
                          }}>{o.label}</p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: C.muted, margin: 0 }}>
                商品款式、色彩选择等其余模块即将上线。
              </p>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: C.muted, marginBottom: '16px' }}>个人时尚选择尚未建立</p>
              <Link to="/test/fashion" style={{ display: 'inline-block', background: C.gold, color: '#fff', padding: '10px 24px', fontFamily: 'Inter, sans-serif', fontSize: '12px', letterSpacing: '2px', textDecoration: 'none', borderRadius: '4px' }}>
                开始理想形象测试
              </Link>
            </div>
          )}
        </div>

        {profile && (
          <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '28px', marginBottom: '24px' }}>
            <SectionTitle label="补充信息" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1px', background: C.border }}>
              {[
                { label: '年龄段', value: profile?.ageRange },
                { label: '预算区间', value: profile?.budget },
                { label: '肤色倾向', value: profile?.skinTone },
              ].map((item, i) => (
                <div key={i} style={{ background: '#fff', padding: '20px' }}>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', letterSpacing: '2px', color: C.muted, marginBottom: '8px' }}>{item.label}</p>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.h1, margin: 0 }}>{item.value || '未填写'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          {[
            { to: '/test/body', label: '风格测试', desc: '体型 + 五官 · 13型判定', done: !!styleResult, locked: false },
            { to: '/test/color', label: '色彩测试', desc: '冷暖 → 五季 → 25季', done: !!finalSeason25, locked: false },
            { to: '/test/fashion', label: '个人时尚选择', desc: '理想形象 · 第一部分', done: !!fashionResult, locked: false },
            {
              to: isSubscribed ? '/profile' : '/subscribe',
              label: '风格档案图谱',
              desc: isSubscribed ? '完整档案总览（本页）' : '订阅解锁品牌匹配与专属优惠',
              done: false,
              locked: !isSubscribed,
            },
          ].map(item => (
            <Link key={item.label} to={item.to} style={{
              display: 'block', padding: '20px',
              background: '#fff', border: `1px solid ${item.done || item.locked ? C.gold : C.border}`,
              borderRadius: '8px', textDecoration: 'none',
              transition: 'border-color .2s',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                <p style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: C.h1, margin: 0 }}>{item.label}</p>
                {item.done && <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '9px', color: C.gold, border: `1px solid ${C.gold}`, padding: '2px 6px', letterSpacing: '1px' }}>✓ 已完成</span>}
                {item.locked && <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '9px', color: C.gold, border: `1px solid ${C.gold}`, padding: '2px 6px', letterSpacing: '1px' }}>🔒 需订阅</span>}
              </div>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: C.muted, margin: 0 }}>{item.desc}</p>
            </Link>
          ))}
        </div>

      </div>
    </div>
  )
}
